

import THREE from './three.js';
import MTLLoader from './mtl-loader.js';
import OBJLoader from './obj-loader.js';

class Preloader {

  constructor() {

    this._assets = {};
    this._progress = 0;
    this._updateHandlers = [];

  }

  _updateProgress() {
    
    const names = Object.keys( this._assets );
    const ratio = 1 / names.length;

    let progress = 0;
    for ( let name of names ) progress += ratio * this._assets[ name ].progress;
    this._progress = progress;

    for ( let handler of this._updateHandlers ) handler( this._progress );

  }

  onUpdate( handler ) {
    
    if ( typeof handler !== 'function' )
      throw new Error( `Preloader.onUpdate must be given a function` );

    this._updateHandlers.push( handler );

  }

  load( options ) {

    if ( !options.hasOwnProperty( 'type' ) )
      throw new Error( `Preloader.load must be given a 'type' property` );

    if ( !options.hasOwnProperty( 'name' ) )
      throw new Error( `Preloader.load must be given a 'name' property` );

    switch (options.type) {
      
      case 'texture':
        this.loadTexture( options );
        break;

      case 'obj':
        this.loadOBJ( options );
        break;

      case 'obj-with-mtl':
        this.loadOBJMTL( options );
        break;

      case 'json':
        this.loadJSON( options );
        break;

    }

  }

  loadTexture( options ) {

    if ( !options.hasOwnProperty( 'url' ) )
      throw new Error( `Preloader.load must be given a 'url' property when type 'texture' is chosen` );

    this._assets[ options.name ] = { progress: 0, promise: null };

    this._assets[ options.name ].promise = new Promise( ( resolve, reject ) => {

      const textureLoader = new THREE.TextureLoader();

      const onLoad = texture => {

        this._assets[ options.name ].progress = 1;
        this._updateProgress();
        resolve( texture );

      };

      const onProgress = event => {

        if ( event.lengthComputable ) this._assets[ options.name ].progress = event.loaded / event.total;
        this._updateProgress();

      };

      const onError = event => reject( new Error( `Failed to load '${ options.name }' from '${ options.url }'` ) );

      textureLoader.load( options.url, onLoad, onProgress, onError );

    } );

  }

  loadOBJ( options ) {

    if ( !options.hasOwnProperty( 'obj' ) )
      throw new Error( `Preloader.load must be given a 'obj' property when type 'obj' is chosen` );

    this._assets[ options.name ] = { progress: 0, promise: null };

    this._assets[ options.name ].promise = new Promise( ( resolve, reject ) => {

      const objLoader = new OBJLoader();

      const onLoad = group => {

        this._assets[ options.name ].progress = 1;
        this._updateProgress();
        resolve( group );

      };

      const onProgress = event => {

        if ( event.lengthComputable ) this._assets[ options.name ].progress = event.loaded / event.total;
        this._updateProgress();

      }

      const onError = event => reject( new Error( `Failed to load OBJ from '${ options.obj }'` ) );

      objLoader.load( options.obj, onLoad, onProgress, onError );
      
    } );

  }

  loadOBJMTL( options ) {

    if ( !options.hasOwnProperty( 'obj' ) )
      throw new Error( `Preloader.load must be given a 'obj' property when type 'obj-with-mtl' is chosen` );

    if ( !options.hasOwnProperty( 'mtl' ) )
      throw new Error( `Preloader.load must be given a 'mtl' property when type 'obj-with-mtl' is chosen` );

    this._assets[ options.name ] = { progress: 0, promise: null };

    this._assets[ options.name ].promise = new Promise( ( resolve, reject ) => {

      const mtlLoader = new MTLLoader();
      const objLoader = new OBJLoader();

      const mtlOnLoad = materials => {
        
        this._assets[ options.name ].progress = 0.5;
        this._updateProgress();

        materials.preload();
        objLoader.setMaterials( materials );
        objLoader.load( options.obj, group => {
        
          this._assets[ options.name ].progress = 1;
          this._updateProgress();
          resolve( group );

        }, objOnProgress, objOnError );

      };

      const mtlOnProgress = event => {

        if ( event.lengthComputable ) this._assets[ options.name ].progress = event.loaded / event.total / 2;
        this._updateProgress();

      }

      const objOnProgress = event => {

        if ( event.lengthComputable ) this._assets[ options.name ].progress = event.loaded / event.total / 2 + 0.5;
        this._updateProgress();

      }

      const mtlOnError = event => reject( new Error( `Failed to load MTL from '${ options.mtl }'` ) );
      const objOnError = event => reject( new Error( `Failed to load OBJ from '${ options.obj }'` ) );

      mtlLoader.load( options.mtl, mtlOnLoad, mtlOnProgress, mtlOnError );
      
    } );

  }

  loadJSON( options ) {

    if ( !options.hasOwnProperty( 'json' ) )
      throw new Error( `Preloader.load must be given a 'json' property when type 'json' is chosen` );

    this._assets[ options.name ] = { progress: 0, promise: null };

    this._assets[ options.name ].promise = new Promise( ( resolve, reject ) => {

      const jsonLoader = new THREE.JSONLoader();

      const onLoad = ( geometry, materials ) => {

        this._assets[ options.name ].progress = 1;
        this._updateProgress();
        resolve( [ geometry, materials ] );

      };

      const onProgress = event => {

        if ( event.lengthComputable ) this._assets[ options.name ].progress = event.loaded / event.total;
        this._updateProgress();

      }

      const onError = event => reject( new Error( `Failed to load JSON from '${ options.json }'` ) );

      jsonLoader.load( options.json, onLoad, onProgress, onError );

    } );

  }

  after( dependencies, then, error ) {

    let assets = [];

    if ( dependencies === 'all' ) {

      assets = Object.keys( this._assets ).map( key => this._assets[ key ].promise );

    }

    else if ( typeof dependencies === 'string' ) {

      if ( this._assets[ dependencies ] === undefined )
        throw new Error( `Unable to locate asset '${ dependencies }' ( argument #1 )` );

      assets = [ this._assets[ dependencies ].promise ];

    }

    else if ( Array.isArray( dependencies ) ) {

        assets = dependencies.map( name => {
        
          if ( this._assets[ name ] === undefined )
            throw new Error( `Unable to locate asset '${ name }' ( argument #1 )` );

          return this._assets[ name ].promise

        } );

    }

    else {

      throw new Error( `Unable to parse assets '${ dependencies }'` );

    }

    if ( typeof then !== 'function' )
      throw new Error( `'then' ( argument #2 ) must be a function` );

    if ( error !== undefined && typeof error !== 'function' )
      throw new Error( `'error' ( argument #3 ) must be a function` );

    Promise.all( assets ).then( values => then( ...values ), error );

  }

}

export default new Preloader();




// WEBPACK FOOTER //
// ./source/scripts/preloader.js